import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { environment } from 'src/environments/environment';
import {
  AttachedChatFile,
  FeedbackObject,
  ChatTypes,
  SharedUser,
  ConversationSettings,
} from './canvas.model';
import { cloneDeep } from 'lodash';
import { Subscription } from 'rxjs';

@Injectable()
export class CanvasApiHandlerService {
  private _subscriptions: Subscription[] = [];

  constructor(private readonly http: HttpClient) {}

  /**
   * Gets the list of chat sessions.
   * @returns An observable for the HTTP response.
   */
  getSessionList() {
    return this.http.get(`${environment.genUrl}api/chat/sessions`);
  }

  /**
   * Update the title of a chat session via an API call.
   * @param session_id - The ID of the session to update.
   * @param title - The new title for the session.
   * @returns An observable for the HTTP response.
   */
  changeSessionTitle(session_id: string, title: string) {
    let headers = new HttpHeaders();
    headers = headers.set('sessionid', `${session_id}`);
    let payload: any = {
      title: title,
    };
    return this.http.put(
      `${environment.genUrl}api/chat/session/${session_id}`,
      payload,
      {
        headers: headers,
      }
    );
  }

  /**
   * Gets the conversation settings via an API call.
   * @returns An observable for the HTTP response.
   */
  getConversationSettings() {
    return this.http.get<ConversationSettings>(
      `${environment.genUrl}api/chat/settings`
    );
  }
  getAppSettings() {
    return this.http.post(`${environment.genUrl}api/app_settings/settings`, {
      settings_name: 'User_guide_video_url',
    });
  }
  /**
   * Deletes a chat session via an API call.
   * @param session - The session to delete.
   * @returns An observable for the HTTP response.
   */
  deleteSessionAPi(session: any) {
    return this.http.delete(
      `${environment.genUrl}api/chat/session/${session._id}`
    );
  }

  /**
   * Deletes a chat session via an API call.
   * @param session - The session to delete.
   * @returns An observable for the HTTP response.
   */
  getUsersList(searchTerm: string) {
    return this.http.get(`${environment.genUrl}api/canvas/share/${searchTerm}`);
  }

  /**
   * Gets the list of chat messages for a specific session.
   * @param session - The session to fetch messages for.
   * @returns An observable for the HTTP response.
   */
  getConversationMessages(session: any) {
    return this.http.get(
      `${environment.genUrl}api/chat/session/${session._id}/conversations`
    );
  }

  /**
   * Gets the list of chat messages for a specific session.
   * @param sessionId - The ID of the chat session.
   * @returns An observable for the HTTP response.
   */
  postChatMessage(payload: any, sessionId: string | undefined) {
    let headers = new HttpHeaders();
    headers = headers.set('sessionid', `${sessionId}`);

    return this.http.post(`${environment.genUrl}api/chat/`, payload, {
      headers: headers,
    });
  }

  /**
   * Regenerates a chat response via an API call.
   * @param chatItem - The chat item to regenerate.
   * @param sessionId - The ID of the chat session.
   * @returns An observable for the HTTP response.
   */
  regenerateChatResponse(chatItem: any, sessionId: string | undefined) {
    let headers = new HttpHeaders();
    headers = headers.set('sessionid', `${sessionId}`);
    return this.http.post(
      `${environment.genUrl}api/chat/conversation/${chatItem.conversationid}/regenerate`,
      chatItem,
      {
        headers: headers,
      }
    );
  }

  /**
   * Bookmarks a chat item
   * @param id - The chat item ID to bookmark
   * @param action - The action verb to toogle marking 'add|remove'
   * @param sessionId - The ID of the chat session.
   * @returns An observable for the HTTP response.
   */
  bookmarkChatItem(id: string, action: string, sessionId: string | undefined) {
    let headers = new HttpHeaders();
    headers = headers.set('sessionid', `${sessionId}`);
    return this.http.put(
      `${environment.genUrl}api/chat/conversation/${id}/bookmark/${action}`,
      undefined,
      {
        headers: headers,
      }
    );
  }

  /**
   * Stops the generation of a chat response.
   * @param conversation_id - The ID of the conversation to stop.
   * @returns An observable for the HTTP response.
   */
  stopGeneration(conversation_id: string, session_id: string) {
    return this.http.post(
      `${environment.genUrl}api/chat/conversation/${conversation_id}/cancel?session_id=${session_id}`,
      {}
    );
  }

  /**
   * Gets the details of a specific document.
   * @param document - The document to fetch details for.
   * @returns An observable for the HTTP response.
   */
  getDocumentDetails(document: any) {
    return this.http.get(
      `${environment.genUrl}api/search/document/${document.index_name}/${document.document_id}`
    );
  }

  /**
   * Gets the content of a specific document.
   * @param document_id - The ID of the document to fetch content for.
   * @returns An observable for the HTTP response.
   */
  getDocumentContent(document_id: string) {
    return this.http.get(
      `${environment.genUrl}api/chat/document/${document_id}`
    );
  }

  /**
   * Gets the details of a specific document version.
   * @param document_id - The ID of the document to fetch details for.
   * @param version - The version number of the document.
   * @returns An observable for the HTTP response.
   */
  getDocumentDetailsByVersion(document_id: string, version: number) {
    return this.http.get(
      `${environment.genUrl}api/chat/document/${document_id}/${version}`
    );
  }

  /**
   * Gets the details of a local Excel dataset.
   * @param excel_dataset_id - The ID of the Excel dataset to fetch details for.
   * @returns An observable for the HTTP response.
   */
  getLocalExcelDetails(excel_dataset_id: string) {
    return this.http.get(
      `${environment.genUrl}api/local/datasets/${excel_dataset_id}`
    );
  }

  /**
   * Gets the conversation by its ID.
   * @param conversation_id - The ID of the conversation to fetch.
   * @returns An observable for the HTTP response.
   */
  getConversationById(conversation_id: string) {
    return this.http.get(
      `${environment.genUrl}api/chat/conversation/${conversation_id}`
    );
  }

  /**
   * Saves a new version of a document by sending a POST request to the server.
   *
   * @param payload - An object containing the title and content of the document.
   * @param payload.title - The title of the document.
   * @param payload.content - The content of the document.
   * @param document_id - The unique identifier of the document.
   * @param sessionId - The session ID for authentication, if available.
   * @returns An Observable of the HTTP response from the server.
   */
  saveDocumentVersion(
    payload: { title: any; content: string },
    document_id: string,
    sessionId: string | undefined
  ) {
    let headers = new HttpHeaders();
    headers = headers.set('sessionid', `${sessionId}`);
    return this.http.post(
      `${environment.genUrl}api/chat/${document_id}/version`,
      payload,
      {
        headers: headers,
      }
    );
  }

  /**
   * Uploads documents via an API call.
   * @param fileLookups - The list of files to upload.
   * @param sessionId - The ID of the chat session.
   * @returns An observable for the HTTP response.
   */
  uploadDocuments(
    fileLookups: AttachedChatFile[],
    sessionId: string | undefined
  ) {
    const formData: FormData = new FormData();
    let filesDetailsArray = cloneDeep(fileLookups);
    for (let fileDetail of filesDetailsArray) {
      if (fileDetail.file) {
        formData.append('files', fileDetail.file);
      }
    }
    let metaData = filesDetailsArray.map((it: AttachedChatFile) => {
      delete it.file;
      delete it.id;
      it.linked_sections =
        it.linked_sections?.map((it: any) => it.identifier) ?? [];
      return it;
    });
    formData.append('filemetadata', JSON.stringify(metaData));

    let headers = new HttpHeaders();
    headers = headers.set('sessionid', `${sessionId}`);

    return this.http.post(
      `${environment.genUrl}api/chat/file/upload`,
      formData,
      {
        headers: headers,
      }
    );
  }

  /**
   * Sends a canvas query via an API call.
   * @param payload - The payload containing the query data.
   * @param document_id - The ID of the document to send the query to.
   * @param sessionId - The ID of the chat session.
   */
  sendCanvasQuery(
    payload: { type: ChatTypes; selected_text: string; prompt: string },
    document_id: any,
    sessionId: string | undefined
  ) {
    let headers = new HttpHeaders();
    headers = headers.set('sessionid', `${sessionId}`);
    return this.http.post(
      `${environment.genUrl}api/chat/canvas/${document_id}/action`,
      payload,
      {
        headers: headers,
      }
    );
  }

  /**
   * Sends feedback comments via an API call.
   * @param payload - The payload containing feedback data.
   * @param sessionId - The ID of the chat session.
   * @returns An observable for the HTTP response.
   */
  sendFeedbackComment(payload: FeedbackObject, sessionId: string | undefined) {
    const headers = new HttpHeaders({
      sessionid: sessionId ?? '',
    });
    return this.http.post(
      `${environment.genUrl}api/chat/conversation/${payload.conversation_id}/response/${payload.response_id}/feedback`,
      payload,
      { headers }
    );
  }

  /**
   * Updates the shared list of users for a session.
   * @param userPayload - The payload containing user data to update.
   * @param sessionId - The ID of the session to update.
   * @returns An observable for the HTTP response.
   */
  updateSharedList(
    userPayload: { users: SharedUser[] },
    sessionId: string | undefined
  ) {
    return this.http.put(
      `${environment.genUrl}api/chat/session/${sessionId}/share`,
      userPayload
    );
  }

  /**
   * Downloads a document by its ID.
   * @param documentid - The ID of the document to download.
   * @returns An observable for the HTTP response.
   */
  downloadDocument(documentid: string) {
    return this.http.get(
      `${environment.genUrl}api/chat/document-download/${documentid}`,
      {
        responseType: 'arraybuffer',
        observe: 'response',
      }
    );
  }

  /**
   * Pushes a new subscription to the list of subscriptions.
   * @param subscription - The subscription to add.
   */
  pushSubscription(subscription: Subscription) {
    if (!this._subscriptions) {
      this._subscriptions = [];
    }
    this._subscriptions.push(subscription);
  }

  /**
   * Clears all subscriptions to prevent memory leaks.
   */
  clearSubscriptions() {
    if (this._subscriptions) {
      this._subscriptions.forEach((subscription) => {
        subscription.unsubscribe();
      });
      this._subscriptions = [];
    }
  }
}
